import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@shared/context/AuthContext";
import { useSettingsSafe } from "@shared/contexts/SettingsContext";

const COLOR_VISION_MODES = new Set([
  "none",
  "protanopia",
  "deuteranopia",
  "tritanopia",
]);

function normalizeTheme(theme: string, highContrast: boolean): "light" | "dark" | "high-contrast" {
  if (highContrast) return "high-contrast";
  return theme === "dark" ? "dark" : "light";
}

function normalizeFontSize(fontSize: string): "small" | "medium" | "large" | "xlarge" {
  if (fontSize === "small" || fontSize === "medium" || fontSize === "large") {
    return fontSize;
  }
  if (fontSize === "extra-large") return "xlarge";
  return fontSize === "xlarge" ? "xlarge" : "medium";
}

function normalizeColorVisionMode(mode: unknown): "none" | "protanopia" | "deuteranopia" | "tritanopia" {
  if (typeof mode !== "string") return "none";
  return COLOR_VISION_MODES.has(mode)
    ? (mode as "none" | "protanopia" | "deuteranopia" | "tritanopia")
    : "none";
}

const SettingsDomApplier: React.FC = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { settings } = useSettingsSafe();

  useEffect(() => {
    if (authLoading || !isAuthenticated || typeof document === "undefined") {
      return;
    }

    const effectiveTheme = normalizeTheme(settings.theme, settings.high_contrast);
    const effectiveFontSize = normalizeFontSize(settings.font_size);
    const colorVisionMode = normalizeColorVisionMode(settings.color_vision_mode);

    document.documentElement.setAttribute("data-theme", effectiveTheme);
    document.documentElement.setAttribute("data-font-size", effectiveFontSize);
    document.documentElement.setAttribute("data-high-contrast", String(settings.high_contrast));
    document.documentElement.setAttribute("data-reduced-motion", String(settings.reduce_motion));
    document.documentElement.setAttribute("data-screen-reader", String(settings.screen_reader));

    if (colorVisionMode === "none") {
      document.documentElement.removeAttribute("data-color-blind-mode");
    } else {
      document.documentElement.setAttribute("data-color-blind-mode", colorVisionMode);
    }

    document.body.classList.remove("theme-light", "theme-dark", "theme-high-contrast");
    document.body.classList.add(`theme-${effectiveTheme}`);

    document.body.classList.remove("light-theme", "dark-theme");
    if (effectiveTheme === "dark") {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.add("light-theme");
    }

    document.body.classList.remove(
      "font-size-small",
      "font-size-medium",
      "font-size-large",
      "font-size-xlarge",
      "font-small",
      "font-medium",
      "font-large",
    );
    document.body.classList.add(`font-size-${effectiveFontSize}`);
    document.body.classList.add(
      effectiveFontSize === "xlarge" ? "font-large" : `font-${effectiveFontSize}`,
    );

    document.body.classList.remove(
      "colorblind-protanopia",
      "colorblind-deuteranopia",
      "colorblind-tritanopia",
    );
    if (colorVisionMode !== "none") {
      document.body.classList.add(`colorblind-${colorVisionMode}`);
    }
  }, [
    authLoading,
    isAuthenticated,
    settings.color_vision_mode,
    settings.font_size,
    settings.high_contrast,
    settings.reduce_motion,
    settings.screen_reader,
    settings.theme,
  ]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || typeof document === "undefined") {
      return;
    }

    const localLanguage =
      typeof window !== "undefined"
        ? window.localStorage.getItem("i18nextLng")
        : null;
    const targetLang = localLanguage || settings.language || "en";
    if (i18n.language !== targetLang) {
      void i18n.changeLanguage(targetLang);
    }
    document.documentElement.lang = targetLang;
  }, [authLoading, i18n, isAuthenticated, settings.language]);

  return null;
};

export default SettingsDomApplier;
